/**
 * Générateur — Définir les nombres relatifs.
 * Attendu ID : 702 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 1)
 *
 * Plan de cartes (validé : mapping O12→702, SVG par construction, compte annoncé variable) :
 *
 *   Carte « mots » — Ex.6 Le mot qui convient
 *     texte_trou_drag : étiquettes = LA liste imposée du doc (positif, négatif,
 *     plus, relatifs, opposé, moins) ; les mots non attendus sont les
 *     distracteurs naturels. Découpage Duolingo : 1 sous-question (a-e) par
 *     tirage. ADAPTATION d : « 0 est à la fois positif et négatif » a un ordre
 *     de mots interchangeable → UN seul trou (le mot troué tourne).
 *
 *   Carte « thermo » — Ex.2 Chaud et froid
 *     texte (saisie) + qcm_unique. Thermomètre SVG GÉNÉRÉ (tube, graduations,
 *     colonne rouge) — 2 régimes de finesse (graduation à l'unité / au
 *     dixième, cf. doc « difficulté croissante »). Saisie : écriture imposée
 *     en consigne (signe « - » clavier, pas de + pour les positifs).
 *
 *   Carte « ecritures » — Créé n°8 Écritures d'un même nombre
 *     qcm_multiple à compte annoncé VARIABLE (2 ou 3 selon tirage). Cible la
 *     convention : le + se supprime, le − jamais ; « l'opposé de -n » = n.
 *
 *   Carte « definitions » — Créé n°9 Vrai ou faux sur les relatifs
 *     tableau_vf : pool d'affirmations à nombres tirés, vérités par
 *     construction, mélange V/F garanti.
 *
 * Politique du cluster : signe « - » = tiret clavier PARTOUT (jamais U+2212) ;
 * aucune règle de calcul sur les relatifs ; « valeur absolue » en mots.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 702;

const L_MOTS = 'Le mot qui convient';
const L_THERMO = 'Chaud et froid (thermomètres)';
const L_ECRITURES = 'Écritures d\'un même nombre';
const L_DEFS = 'Vrai ou faux sur les relatifs';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Valeurs manipulées en DIXIÈMES entiers (zéro flottant). dix(-83) = "-8,3".
const dix = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};

const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

// ═════════ Carte 1 — Le mot qui convient (Ex.6) ═════════

const LISTE_MOTS = ['positif', 'négatif', 'plus', 'relatifs', 'opposé', 'moins'];

/** Un nombre relatif « d'affichage » aléatoire (dixièmes). */
const nombreAffiche = () => {
    const entier = randomChoice([true, false]);
    const v = entier ? randInt(1, 25) * 10 : randInt(11, 199);
    return randomChoice([true, false]) ? dix(v) : dix(-v);
};

const genMots = () => {
    const variante = randomChoice(['a', 'b', 'c', 'd', 'e']);
    let phrase;
    if (variante === 'a') {
        const uns = new Set();
        for (let essai = 0; essai < 40 && uns.size < 4; essai++) uns.add(nombreAffiche());
        ['-3', '5', '-9,3', '100,7'].forEach(x => { if (uns.size < 4) uns.add(x); }); // repli (valeurs du doc)
        phrase = `${[...uns].join(' ; ')} et 0 sont des nombres {{relatifs}}.`;
    } else if (variante === 'b') {
        const n = randInt(2, 15);
        phrase = `Le nombre +${n} est un nombre {{positif}}. Il peut aussi s'écrire sans le signe {{plus}}.`;
    } else if (variante === 'c') {
        const n = randInt(2, 15);
        phrase = `Le nombre (-${n}) est un nombre {{négatif}}. On ne peut pas supprimer le signe {{moins}}.`;
    } else if (variante === 'd') {
        phrase = randomChoice([
            'Le nombre 0 est à la fois {{positif}} et négatif.',
            'Le nombre 0 est à la fois positif et {{négatif}}.',
        ]);
    } else {
        const d = dix(randInt(11, 99));
        phrase = `(-${d}) est l'{{opposé}} de ${d}.`;
    }
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète avec le mot qui convient.</b></p>',
        { ...opts(L_MOTS), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, LISTE_MOTS) });
    payload._meta.params = { variante };
    return payload;
};

// ═════════ Carte 2 — Thermomètres (Ex.2) ═════════

/**
 * Tire un thermomètre : régime 'entier' (graduation 1 °C, plage -10..10) ou
 * 'dixieme' (graduation 0,1 °C, plage de 2 °C). La température tombe TOUJOURS
 * sur une graduation, jamais sur une borne.
 */
const tirerThermo = () => {
    if (randomChoice([true, false])) {
        // graduations à l'unité, étiquettes tous les 5 °C
        let t = 0;
        while (t === 0) t = randInt(-9, 9) * 10;
        return { min: -100, max: 100, pas: 10, etiq: 50, t };
    }
    // graduations au dixième, étiquettes aux entiers — température non entière
    const base = randInt(-5, 3) * 10;
    let k = randInt(1, 19);
    if (k % 10 === 0) k += 1;
    return { min: base, max: base + 20, pas: 1, etiq: 10, t: base + k };
};

/** SVG thermomètre — parsable : étiquettes <text class='tl'>, mercure <rect class='hg'>. */
const svgThermo = (cfg) => {
    const W = 150, H = 300, yBas = 240, yHaut = 30, xTube = 78;
    const Y = (v) => Math.round((yBas - ((v - cfg.min) / (cfg.max - cfg.min)) * (yBas - yHaut)) * 10) / 10;
    let grads = '';
    for (let v = cfg.min; v <= cfg.max; v += cfg.pas) {
        const y = Y(v);
        const majeure = v % cfg.etiq === 0;
        grads += `<line x1='${xTube - (majeure ? 14 : 8)}' y1='${y}' x2='${xTube - 2}' y2='${y}' stroke='currentColor' stroke-width='${majeure ? 1.4 : 0.7}'/>`;
        if (majeure) {
            grads += `<text class='tl' x='${xTube - 18}' y='${y}' dy='4' font-size='12' text-anchor='end' fill='currentColor'>${dix(v)}</text>`;
        }
    }
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}' viewBox='0 0 ${W} ${H}' font-family='sans-serif'>`
        + `<rect x='${xTube}' y='${yHaut - 8}' width='16' height='${yBas - yHaut + 16}' rx='8' fill='none' stroke='currentColor' stroke-width='1.6'/>`
        + `<circle cx='${xTube + 8}' cy='${yBas + 26}' r='15' fill='#dc2626'/>`
        + `<rect class='hg' x='${xTube + 4}' y='${Y(cfg.t)}' width='8' height='${Math.round((yBas + 20 - Y(cfg.t)) * 10) / 10}' fill='#dc2626'/>`
        + grads
        + `<text x='${xTube + 26}' y='${yHaut + 6}' font-size='12' fill='currentColor'>°C</text>`
        + `</svg>`;
};

const genThermoSaisie = () => {
    const cfg = tirerThermo();
    const payload = makeSaisie(dix(cfg.t),
        '<p><b>Quelle température, en °C, ce thermomètre indique-t-il ?</b> '
        + 'Écris le nombre avec son signe s\'il est négatif (ex. : -4 ou -1,5), sans signe sinon (ex. : 7).</p>',
        { ...opts(L_THERMO), suffixe: '°C', elements_additionnels: svgToElement(svgThermo(cfg)) });
    payload._meta.params = { t: cfg.t, pas: cfg.pas };
    return payload;
};

const genThermoQcm = () => {
    const cfg = tirerThermo();
    const correct = dix(cfg.t);
    const candidats = [dix(-cfg.t), dix(cfg.t + cfg.pas), dix(cfg.t - cfg.pas), dix(cfg.t + 2 * cfg.pas)];
    const distracteurs = [];
    candidats.forEach(c => {
        if (distracteurs.length < 3 && c !== correct && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs], correct,
        '<p><b>Quelle température, en °C, ce thermomètre indique-t-il ?</b></p>',
        { ...opts(L_THERMO), elements_additionnels: svgToElement(svgThermo(cfg)) });
    payload._meta.params = { t: cfg.t, pas: cfg.pas };
    return payload;
};

// ═════════ Carte 3 — Écritures d'un même nombre (Créé n°8) ═════════

const MOTS_COMPTE = { 2: 'deux', 3: 'trois' };

const genEcritures = () => {
    const v = randomChoice([true, false]) ? randInt(2, 19) * 10 : randInt(15, 95);
    const s = dix(v); // écriture positive sans signe
    const negatif = randomChoice([true, false]);

    let cible, corrects, faux;
    if (!negatif) {
        cible = `+${s}`;
        corrects = [s, `(+${s})`, `l'opposé de -${s}`];
        faux = shuffle([`-${s}`, `l'opposé de ${s}`, `(-${s})`]).slice(0, 2);
    } else {
        cible = `-${s}`;
        corrects = shuffle([`(-${s})`, `l'opposé de ${s}`, `l'opposé de +${s}`]).slice(0, randomChoice([2, 3]));
        faux = shuffle([s, `l'opposé de -${s}`, `+${s}`]).slice(0, 5 - corrects.length);
    }
    const payload = makeQcm([...corrects, ...faux], corrects,
        `<p><b>Coche les ${MOTS_COMPTE[corrects.length]} écritures qui désignent le même nombre que ${cible}.</b></p>`,
        opts(L_ECRITURES), true);
    payload._meta.params = { cible, corrects };
    return payload;
};

// ═════════ Carte 4 — Vrai ou faux sur les relatifs (Créé n°9) ═════════

const genDefinitionsVf = () => {
    const n = randInt(2, 30);
    const m = randInt(2, 30);
    let dNonEntier = randInt(11, 99);
    if (dNonEntier % 10 === 0) dNonEntier += 3;
    const d = dix(dNonEntier);

    const vraies = [
        [`-${n} est un nombre relatif.`, 0],
        ['0 est un nombre relatif.', 0],
        [`${m} et +${m} désignent le même nombre.`, 0],
        [`+${d} est un nombre relatif.`, 0],
    ];
    const fausses = [
        ['Un nombre écrit sans signe est un nombre négatif.', 1],
        [`-${d} est un nombre entier.`, 1],
        [`-${n} et ${n} désignent le même nombre.`, 1],
    ];
    // 2 vraies + 2 fausses tirées → mélange garanti par construction
    const lignes = shuffle([...shuffle(vraies).slice(0, 2), ...shuffle(fausses).slice(0, 2)]);
    const payload = makeVraiFaux(
        lignes.map(l => l[0]),
        lignes.map(l => l[1]),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_DEFS));
    payload._meta.params = { n, m };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    mots_texte_trou_drag: genMots,
    thermo_texte: genThermoSaisie,
    thermo_qcm_unique: genThermoQcm,
    ecritures_qcm_multiple: genEcritures,
    definitions_vf: genDefinitionsVf,
};

const FORMAT_LABELS = {
    texte_trou_drag: 'Drag', texte: 'Saisie', qcm_unique: 'QCM',
    qcm_multiple: 'QCM Multi', vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'mots', label: L_MOTS, formatsList: buildFormats('mots') },
    { id: 'thermo', label: L_THERMO, formatsList: buildFormats('thermo') },
    { id: 'ecritures', label: L_ECRITURES, formatsList: buildFormats('ecritures') },
    { id: 'definitions', label: L_DEFS, formatsList: buildFormats('definitions') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
